import { useRef, useState } from 'react'

/**
 * Photo slider for a trailer.
 *
 * No carousel library — it's one state variable and a translated strip, which
 * keeps the bundle small and means nothing to update later. Arrows and dots
 * for desktop, swipe for phones, since most of the traffic here is on a phone.
 *
 * Falls back to a single static image when a trailer only has one photo.
 */
export default function PhotoSlider({ images = [], alt = '' }) {
  const [index, setIndex] = useState(0)
  const touchStart = useRef(null)

  if (images.length === 0) return null

  if (images.length === 1) {
    return (
      <div className="slider">
        <img src={images[0]} alt={alt} width="1400" height="788" />
      </div>
    )
  }

  const go = (next) => {
    const count = images.length
    setIndex(((next % count) + count) % count) // wraps both directions
  }

  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX
  }

  const onTouchEnd = (e) => {
    if (touchStart.current === null) return
    const delta = e.changedTouches[0].clientX - touchStart.current
    if (Math.abs(delta) > 40) go(delta < 0 ? index + 1 : index - 1)
    touchStart.current = null
  }

  return (
    <div
      className="slider"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div
        className="slider__strip"
        style={{ transform: `translateX(-${index * 100}%)` }}
      >
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${alt} — photo ${i + 1} of ${images.length}`}
            width="1400"
            height="788"
            loading={i === 0 ? 'eager' : 'lazy'}
          />
        ))}
      </div>

      <button
        type="button"
        className="slider__nav slider__nav--prev"
        onClick={() => go(index - 1)}
        aria-label="Previous photo"
      >
        &larr;
      </button>
      <button
        type="button"
        className="slider__nav slider__nav--next"
        onClick={() => go(index + 1)}
        aria-label="Next photo"
      >
        &rarr;
      </button>

      <div className="slider__dots">
        {images.map((src, i) => (
          <button
            key={src}
            type="button"
            className={`slider__dot ${i === index ? 'is-on' : ''}`}
            onClick={() => go(i)}
            aria-label={`Photo ${i + 1}`}
            aria-current={i === index}
          />
        ))}
      </div>
    </div>
  )
}
